import React, { useState, useEffect } from 'react';
import { getGroupPerformanceAsync } from '../../Services/ReportService';
import { clientListAsync } from '../../Services/AdminService';
import Card from '../../Shared/Components/Card/Card';
import Header from '../../Shared/Components/Header/Header';
import Loading from '../../Shared/Loading';
import AiOverviewCard from './Components/AiOverviewCard';
import { BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import moment from 'moment';

const GroupPerformance = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState('');

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await clientListAsync();
                setClients(response.clients || []);
            } catch (error) {
                console.error("Error fetching clients:", error);
            }
        };

        fetchClients();
    }, []);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await getGroupPerformanceAsync(
                selectedGroupId || null,
                selectedClientId || null
            );
            setData(response);
        } catch (err) {
            console.error("Error fetching group performance:", err);
            setError('Failed to load group performance data');
        } finally {
            setLoading(false);
        }
    }, [selectedClientId, selectedGroupId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading && !data) {
        return (
            <>
                <Header heading="Group Performance" subheading="Loading..." icon="stats-up" />
                <div className="container-fluid content">
                    <Card>
                        <Loading />
                    </Card>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header heading="Group Performance" subheading="Error" icon="stats-up" />
                <div className="container-fluid content">
                    <Card>
                        <div className="alert alert-danger">{error}</div>
                    </Card>
                </div>
            </>
        );
    }

    // Get available groups from the data for filtering
    const availableGroups = data?.groups || [];

    // Prepare data for comparison charts
    const comparisonData = data?.groups?.map(group => ({
        name: group.groupName,
        completionRate: group.courses.completionRate,
        averageScore: group.performance.averageScore,
        activityRate: group.learners.activityRate,
        engagementScore: group.performance.engagementScore
    })) || [];

    return (
        <>
            <Header
                heading="Group Performance"
                subheading="Compare and analyze group performance"
                icon="stats-up"
            />
            <div className="container-fluid content">
                <AiOverviewCard aiOverview={data?.aiOverview} />
                {/* Filters */}
                <Card title="Filters">
                    <div className="row">
                        <div className="col-md-4">
                            <label className="form-label">Client</label>
                            <select
                                className="form-control"
                                value={selectedClientId}
                                onChange={(e) => {
                                    setSelectedClientId(e.target.value);
                                    setSelectedGroupId(''); // Reset group when client changes
                                }}
                            >
                                <option value="">All Clients</option>
                                {clients.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Group</label>
                            <select
                                className="form-control"
                                value={selectedGroupId}
                                onChange={(e) => setSelectedGroupId(e.target.value)}
                            >
                                <option value="">All Groups</option>
                                {availableGroups.map(g => (
                                    <option key={g.groupId} value={g.groupId}>{g.groupName}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4 d-flex align-items-end">
                            <button
                                className="btn btn-primary"
                                onClick={fetchData}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Refresh'}
                            </button>
                        </div>
                    </div>
                </Card>

                {data && data.groups && data.groups.length > 0 ? (
                    <>
                        {/* Best Performers Summary */}
                        {data.comparison && (
                            <div className="row">
                                <div className="col-md-4">
                                    <Card title="Best Performing Group">
                                        {data.comparison.bestPerforming ? (
                                            <div className="text-center">
                                                <h4>{data.comparison.bestPerforming.groupName}</h4>
                                                <p className="text-muted mb-0">
                                                    Avg Score: <strong>{data.comparison.bestPerforming.performance?.averageScore?.toFixed(1)}</strong>
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="text-muted">No data</p>
                                        )}
                                    </Card>
                                </div>
                                <div className="col-md-4">
                                    <Card title="Most Active Group">
                                        {data.comparison.mostActive ? (
                                            <div className="text-center">
                                                <h4>{data.comparison.mostActive.groupName}</h4>
                                                <p className="text-muted mb-0">
                                                    Activity Rate: <strong>{data.comparison.mostActive.learners?.activityRate?.toFixed(1)}%</strong>
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="text-muted">No data</p>
                                        )}
                                    </Card>
                                </div>
                                <div className="col-md-4">
                                    <Card title="Highest Completion Rate">
                                        {data.comparison.highestCompletion ? (
                                            <div className="text-center">
                                                <h4>{data.comparison.highestCompletion.groupName}</h4>
                                                <p className="text-muted mb-0">
                                                    Completion: <strong>{data.comparison.highestCompletion.courses?.completionRate?.toFixed(1)}%</strong>
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="text-muted">No data</p>
                                        )}
                                    </Card>
                                </div>
                            </div>
                        )}

                        {/* Performance Comparison Charts */}
                        {comparisonData.length > 1 && (
                            <>
                                <Card title="Group Performance Comparison">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <BarChart data={comparisonData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="completionRate" fill="#8884d8" name="Completion Rate (%)" />
                                            <Bar dataKey="averageScore" fill="#82ca9d" name="Average Score" />
                                            <Bar dataKey="activityRate" fill="#ffc658" name="Activity Rate (%)" />
                                            <Bar dataKey="engagementScore" fill="#ff7c7c" name="Engagement Score" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Card>

                                <Card title="Performance Radar Comparison">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <RadarChart data={comparisonData}>
                                            <PolarGrid />
                                            <PolarAngleAxis dataKey="name" />
                                            <PolarRadiusAxis />
                                            <Radar name="Completion Rate" dataKey="completionRate" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                                            <Radar name="Average Score" dataKey="averageScore" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                                            <Tooltip />
                                            <Legend />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                </Card>
                            </>
                        )}

                        {/* Individual Group Details */}
                        {data.groups.map((group, index) => (
                            <Card key={index} title={`${group.groupName} - ${group.clientName}`}>
                                <div className="row">
                                    <div className="col-md-12 mb-3">
                                        <small className="text-muted">
                                            Created: {moment(group.createdDate).format('DD/MM/YYYY')} |
                                            Last Activity: {group.learners.lastActivity ? moment(group.learners.lastActivity).format('DD/MM/YYYY HH:mm') : 'N/A'}
                                        </small>
                                    </div>
                                </div>

                                <div className="row">
                                    {/* Learner Stats */}
                                    <div className="col-md-3">
                                        <h6>Learners</h6>
                                        <div className="border-left border-primary pl-3">
                                            <p className="mb-1">
                                                <strong>Total:</strong> {group.learners.totalLearners}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Active:</strong> {group.learners.activeLearners}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Activity Rate:</strong> {group.learners.activityRate.toFixed(1)}%
                                            </p>
                                        </div>
                                    </div>

                                    {/* Course Stats */}
                                    <div className="col-md-3">
                                        <h6>Courses</h6>
                                        <div className="border-left border-success pl-3">
                                            <p className="mb-1">
                                                <strong>Total Enrollments:</strong> {group.courses.totalEnrollments}
                                            </p>
                                            <p className="mb-1 text-success">
                                                <strong>Completed:</strong> {group.courses.completed}
                                            </p>
                                            <p className="mb-1 text-warning">
                                                <strong>In Progress:</strong> {group.courses.inProgress}
                                            </p>
                                            <p className="mb-1 text-secondary">
                                                <strong>Not Started:</strong> {group.courses.notStarted}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Completion Rate:</strong> {group.courses.completionRate.toFixed(1)}%
                                            </p>
                                        </div>
                                    </div>

                                    {/* Performance Stats */}
                                    <div className="col-md-3">
                                        <h6>Performance</h6>
                                        <div className="border-left border-info pl-3">
                                            <p className="mb-1">
                                                <strong>Avg Score:</strong> {group.performance.averageScore.toFixed(1)}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Avg Completion Time:</strong> {group.performance.averageCompletionTime.toFixed(1)}h
                                            </p>
                                            <p className="mb-1">
                                                <strong>Modules Completed:</strong> {group.performance.totalModulesCompleted}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Engagement Score:</strong> {group.performance.engagementScore.toFixed(1)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Top Performers */}
                                    <div className="col-md-3">
                                        <h6>Top Performers</h6>
                                        {group.topPerformers && group.topPerformers.length > 0 ? (
                                            <div className="border-left border-warning pl-3">
                                                {group.topPerformers.slice(0, 5).map((performer, idx) => (
                                                    <p key={idx} className="mb-1 small">
                                                        <strong>{performer.name}</strong><br />
                                                        <span className="text-muted">
                                                            {performer.completions} completed |
                                                            Avg: {performer.averageScore.toFixed(1)}
                                                        </span>
                                                    </p>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-muted small">No top performers data</p>
                                        )}
                                    </div>
                                </div>

                                {/* Progress Bars */}
                                <div className="row mt-3">
                                    <div className="col-md-6">
                                        <label className="small">Course Completion Progress</label>
                                        <div className="progress" style={{ height: '25px' }}>
                                            <div
                                                className="progress-bar bg-success"
                                                style={{ width: `${group.courses.completionRate}%` }}
                                            >
                                                {group.courses.completionRate.toFixed(1)}%
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="small">Learner Activity Rate</label>
                                        <div className="progress" style={{ height: '25px' }}>
                                            <div
                                                className="progress-bar bg-info"
                                                style={{ width: `${group.learners.activityRate}%` }}
                                            >
                                                {group.learners.activityRate.toFixed(1)}%
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </>
                ) : (
                    <Card>
                        <p className="text-muted text-center">No group data available. Please select a client or adjust your filters.</p>
                    </Card>
                )}
            </div>
        </>
    );
};

export default GroupPerformance;
